import { getAuth } from 'firebase/auth'

/**
 * Retrieves the current user's ID token, refreshing it if needed.
 * Ensures Firebase Auth state has resolved first.
 */
export const getIdToken = async (): Promise<string> => {
  const auth = getAuth()
  const user = auth.currentUser

  if (!user) {
    // Wait for auth state to resolve (especially on reload)
    return new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        unsubscribe()
        if (!user) return reject(new Error('User not authenticated'))
        const token = await user.getIdToken()
        resolve(token)
      })
    })
  }

  return await user.getIdToken()
}

/** Get a token only while Firebase still belongs to the user who started an action. */
export const getIdTokenForUid = async (expectedUid: string): Promise<string> => {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user || user.uid !== expectedUid) throw new Error('Signed-in account changed. Please retry.')
  const token = await user.getIdToken()
  if (auth.currentUser?.uid !== expectedUid)
    throw new Error('Signed-in account changed. Please retry.')
  return token
}

/**
 * The uid Firebase will actually authenticate as, waiting for auth to resolve.
 *
 * Deliberately the same resolution getIdToken performs, rather than the React
 * auth context. A caller that compares against the context can be reading null
 * while getIdToken goes on to resolve a real — possibly different — user, which
 * is how a bank could be attached to whoever happened to be signed in rather
 * than whoever started the flow.
 *
 * Null when nobody is signed in.
 */
export const resolveCurrentUid = async (): Promise<string | null> => {
  const auth = getAuth()
  if (auth.currentUser) return auth.currentUser.uid

  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe()
      resolve(user?.uid ?? null)
    })
  })
}
